import axios from "axios";
import Cookies from "js-cookie";
import axiosInstance from "./axios-service/axiosInstance";
import resumeServiceInstance from "./axios-service/resumeServiceInstance";

const headers = {
  Accept: "application/json;odata=nometadata",
};

const isLive =
  typeof window !== "undefined" &&
  window.location.hostname === "talenthub.intelegain.com";

const candidateService = {
  fetchCandidates: () => {
    return axiosInstance.get(
      "/contacts?$select=itl_activitystamp,_itl_requisitionid_value,itl_matchingscore,itl_matchedrequisition,itl_additionaldetails,itl_candidatesummary,itl_employmenttype,itl_candidatecontact,itl_candidatecurrentctc,itl_candidateexpectedctc,itl_candidateexpectedctcto,itl_candidatefeedback,itl_candidatename,itl_candidaterelevantexperiencemonths,itl_candidaterelevantexperienceyears,itl_candidaterole,itl_candidatesource,itl_candidatestatus,itl_candidatetotalexperiencemonths,itl_candidatetotalexperienceyears,createdon,itl_dateofbirth,itl_emailid,fullname,itl_gender,lastname,itl_location,_itl_locationid_value,modifiedon,itl_noticeperiod,itl_reasonforchangeleaving,_itl_recruiter_value,itl_remark,itl_resume,itl_resumelink,_itl_source_value&$expand=itl_LocationID($select=itl_locationname),itl_RoleNew($select=itl_name)&$orderby=itl_activitystamp desc",
      {
        headers: headers,
      }
    );
  },

  addCandidate: (obj) => {
    return axiosInstance.post("/contacts?$select=contactid", obj, {
      headers: {
        ...headers, // Ensure headers are inside the headers object
        Prefer: "odata.include-annotations=*,return=representation",
      },
    });
  },

  fetchCandidateEmailByID: (id) => {
    return axiosInstance.get(`/contacts(${id})?$select=itl_emailid`);
  },

  fetchCandidateDeclarationByID: (id) => {
    return axiosInstance.get(
      `/contacts(${id})?$select=itl_declarationdate,itl_declarationname,itl_declarationplace,itl_declarationsignature`
    );
  },

  fetchCandidateByID: (id) => {
    return axiosInstance.get(
      `/contacts(${id})?$select=contactid,_itl_requisitionid_value,itl_employmenttype,itl_candidatesummary,itl_matchedrequisition,itl_matchingscore,itl_uipayload,itl_candidatecontact,itl_alternatecontact,itl_bloodgroup,itl_presentaddress,itl_permanentaddress,itl_candidatecurrentctc,itl_candidateexpectedctc,itl_candidateexpectedctcto,itl_candidatefeedback,itl_candidatename,itl_candidaterelevantexperiencemonths,itl_candidaterelevantexperienceyears,itl_candidaterole,itl_candidatesource,itl_candidatestatus,itl_candidatetotalexperiencemonths,itl_candidatetotalexperienceyears,itl_dateofbirth,itl_emailid,itl_gender,lastname,itl_location,itl_knowaboutussource,_itl_locationid_value,itl_noticeperiod,
      itl_consultantname,itl_otherspecification,itl_reasonforchangeleaving,itl_formsubmitted,_itl_recruiter_value,itl_remark,itl_additionaldetails,itl_resumelink&$expand=itl_LocationID($select=itl_locationname),itl_Recruiter($select=name),itl_Source($select=itl_sourcemasterid,itl_name,statecode),itl_RoleNew($select=itl_name)`
    );
  },

  addResume: (obj) => {
    return axiosInstance.post("/annotations", obj);
  },

  getResume: (id) => {
    return axiosInstance.get(
      `/contacts?$select=_accountid_value&$expand=Contact_Annotation($select=annotationid,documentbody,filename,isdocument,_objectid_value)&$filter=contactid eq ${id}`,
      headers
    );
  },

  updateResume: (id, obj) => {
    return axiosInstance.patch(`/annotations(${id})`, obj);
  },

  updateCandidate: (id, obj) => {
    return axiosInstance.patch(`/contacts(${id})`, obj, {
      headers: {
        ...headers, // Ensure headers are inside the headers object
        Prefer: "odata.include-annotations=*,return=representation",
      },
    });
  },

  updateCandidateDeclaration: (id, obj) => {
    return axiosInstance.patch(`/contacts(${id})`, obj, {
      headers,
    });
  },
  updateCandidateSignature: (id, signatureFile) => {
    return axiosInstance.patch(
      `/contacts(${id})/itl_candidatesignature`,
      signatureFile,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/octet-stream",
          "x-ms-file-name": signatureFile.name || "signature.png",
        },
      }
    );
  },

  getCandidateSignature: async (id) => {
    try {
      // Fetch file content
      const response = await axiosInstance.get(
        `/contacts(${id})/itl_candidatesignature/$value`,
        {
          responseType: "blob", // important for binary data
          headers: {
            Accept: "application/octet-stream",
          },
        }
      );

      // Convert blob to an object URL
      const fileURL = URL.createObjectURL(response.data);
      return fileURL;
    } catch (error) {
      console.error("Error fetching signature file:", error);
      return null;
    }
  },

  getDeclarationSignature: async (id) => {
    try {
      // Fetch file content
      const response = await axiosInstance.get(
        `/contacts(${id})/itl_candidatesignature/$value`,
        {
          responseType: "blob", // important for binary data
          headers: {
            Accept: "application/octet-stream",
          },
        }
      );

      const blob = response.data;

      // Convert blob to Data URL
      const dataURL = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result); // data URL
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      return dataURL;
    } catch (error) {
      console.error("Error fetching signature file:", error);
      return null;
    }
  },

  saveCandidatePhoto: (id, photo) => {
    return axiosInstance.patch(`/contacts(${id})/itl_candidatephoto`, photo, {
      headers: {
        "Content-Type": "application/octet-stream",
        "If-Match": "*",
        "x-ms-file-name": "candidates_photo.png",
      },
    });
  },

  getCandidatesPhoto: async (id) => {
    try {
      const response = await axiosInstance.get(
        `/contacts(${id})/itl_candidatephoto/$value`,
        {
          responseType: "blob",
          headers: {
            Accept: "image/*",
          },
        }
      );
      if (response.status !== 200) {
        console.error(`Unexpected response status: ${response.status}`);
        return null;
      }
      const fileURL = URL.createObjectURL(response.data);
      return fileURL;
    } catch (error) {
      console.error("Error fetching photo:", error);
      return null;
    }
  },

  deleteCandidate: (id) => {
    return axiosInstance.delete(`/contacts(${id})`);
  },

  sendCandidateOtp: async (data) => {
    //cloud flow name : ITL-Generate OTP
    const API_URL = isLive
      ? "https://317b454404b6e0ec8040763a25d1ae.d9.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/c9396f954ba145518fa2298788b6589b/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=RDY8QAtWyGRLqQF8XihkUAKCHvSIsmJvEXjuMF36AoM"
      : "https://e6bcb97abef1e33a9b36ba1c392e51.de.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/59f2ffa76b324a9c9ff5ce124456d72e/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=lHPIhvij93xFMtudwJxMr1Dgw0M-r_xI3pkIjdhlbFI";
    try {
      const response = await axios.post(API_URL, data, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  getCandidateFeedbackToken: async () => {
    //ITL- Generate Token
    const API_URL = isLive
      ? "https://317b454404b6e0ec8040763a25d1ae.d9.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/b31eeddefa7b4fdba1ed21cbd8ecec05/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=Zizr5pYCA2cHyrpcor6tl9pP_lU0xHM_tGM_6mzoWwY"
      : "https://e6bcb97abef1e33a9b36ba1c392e51.de.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/b904dacb82ba4d75a27f35ffc5489172/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=u04v1rPQ4hgw9xw00dFVhjs263qHp4gsH8ezM6-LOcs";

    try {
      const response = await axios.post(API_URL, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const token = response.data;

      // Store token in cookies
      Cookies.set("token", token, { secure: true, sameSite: "Strict" });
    } catch (error) { }
  },

  verifyCandidateOTP: async (data) => {
    //cloud flow name : ITL-Check OTP
    const API_URL = isLive
      ? "https://317b454404b6e0ec8040763a25d1ae.d9.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/36649458f6db4c4ba463b6e03047babd/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=v8hGU7mS7daEt_bYtKfKqM9kR8IjKYPj5HEtZekcpx0"
      : "https://e6bcb97abef1e33a9b36ba1c392e51.de.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/167a266870314dc7bccfe9874a3fdff4/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=Vf6VeeXMSxEfDLupVT7qLmHdyLSd0YGrlTOrG0D6rA4";

    try {
      const response = await axios.post(API_URL, data, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  generateCandidateLink: async (id) => {
    try {
      const response = await axiosInstance.post("/generateOneTimeToken", {
        candidateId: id,
      });
      const token = response.data.token;
      return `${window.location.origin}/candidatefeedback/${id}?token=${token}`;
    } catch (error) {
      console.error("Error generating link:", error);
    }
  },

  sendCandidateStatusEmail: async (data) => {
    //flow name : ITL- Send Email To HR
    const API_URL = "https://e6bcb97abef1e33a9b36ba1c392e51.de.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/f242146910c24ab798e1486e29a0f117/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=bNvpUGGYZo-3I93UC0aPJfjki8UTGLxwyUWpSVQ2gao";

    try {
      const response = await axios.post(API_URL, data, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      return response;
    } catch (error) { }
  },

  parseResumeData: async (formData) => {


    try {
      const response = await resumeServiceInstance.post(
        "/parse-resume?file",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response;
    } catch (error) { }
  },

  saveResumeTextData: async (obj) => {
    return axiosInstance.post("/itl_resumeintexts", obj, {
      headers: {
        Accept: "application/json",
      },
    });
  },


  setRecruitmentFormExpiry: async (obj) => {
    return axiosInstance.post("/itl_recruitmentformexpirydetailses", obj, {
      headers: { Prefer: "return=representation" },
    });
  },

  updateRecruitmentFormExpiry: async (obj, id) => {
    return axiosInstance.patch(`/itl_recruitmentformexpirydetailses(${id})`, obj, {
      headers: { Prefer: "return=representation" },
    });
  },

  getRecruitmentFormExpiry: async (id) => {
    return axiosInstance.get(
      `/itl_recruitmentformexpirydetailses?$filter=_itl_candidateid_value eq ${id ? `'${id}'` : null
      }`
    );
  },

  getRecruitmentFormExpiryByToken: async (token) => {
    return axiosInstance.get(
      `/itl_recruitmentformexpirydetailses?$filter=itl_token eq '${token}'`
    );
  },
};

export default candidateService;
